'use client';

/**
 * Architecture Flow Widget
 *
 * @component_role Composable System Pipeline Visualization
 * @business_rule Displays packages as pipeline modules with directed flows,
 *   fully data-driven from merged BFF response (/docs/arch/flow).
 * @user_outcome Understand how components connect and what breaks if removed.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain docs
 */

import { memo, useCallback, useMemo, useState, useEffect } from 'react';
import { api } from '@/lib/api/client';
import {
  ReactFlow,
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  Node,
  Edge,
  Handle,
  Position,
  NodeProps,
  BackgroundVariant,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Loader2,
  Network,
  Package,
  Server,
  Database,
  Cpu,
  Shield,
  Zap,
  MessageSquare,
  Search,
  Share2,
  Brain,
  ShoppingCart,
  Activity,
  FileText,
  Layers,
  Bot,
  LayoutDashboard,
  ArrowRightCircle,
  ArrowLeftCircle,
  ArrowLeftRight,
  Mail,
  Lightbulb,
  Plug,
  Puzzle,
  Cog,
  ArrowRight,
  Target,
  BookOpen,
  Settings,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';

// Types - matches /docs/arch/canvas response
interface CanvasNodeLayout {
  column: number;
  row: number;
}

interface ArchNode {
  id: string;
  label: string;
  type: string;  // system, package, capability, feature, story, connector, pipeline
  description?: string;
  icon?: string;
  marketing_name?: string;
  semantic_id?: string;  // Technical ID from Neo4j (e.g., pkg_klyde_edge_abc123)
  synthetic?: boolean;
  layout?: CanvasNodeLayout;
  extra?: Record<string, unknown>;
}

interface ArchEdge {
  id: string;
  source: string;
  target: string;
  type: string;  // HAS_CHILD, DATA_FLOW, CONTEXT_ENRICHMENT, ACTION_EXECUTION, etc.
  label?: string;
}

interface FlowEdgeStyle {
  color: string;
  stroke_width: number;
  stroke_dasharray?: string;
  label: string;
  description: string;
}

interface ArchData {
  nodes: ArchNode[];
  edges: ArchEdge[];
  metadata: {
    node_count: number;
    edge_count: number;
    packages: number;
    capabilities: number;
    features: number;
    stories: number;
    synthetic_count: number;
  };
  flow_edge_styles?: Record<string, FlowEdgeStyle>;
}

// Icon name to component mapping
const ICON_MAP: Record<string, LucideIcon> = {
  Server,
  Database,
  Cpu,
  Shield,
  Zap,
  MessageSquare,
  Search,
  Share2,
  Brain,
  ShoppingCart,
  Activity,
  FileText,
  Layers,
  Bot,
  LayoutDashboard,
  Package,
  Network,
  ArrowRightCircle,
  ArrowLeftCircle,
  ArrowLeftRight,
  Mail,
  Lightbulb,
  Plug,
  Puzzle,
  Cog,
  ArrowRight,
  Target,
  BookOpen,
  Settings,
};

function getIconComponent(iconName?: string): LucideIcon {
  if (!iconName) return Package;
  return ICON_MAP[iconName] || Package;
}

// =============================================================================
// MODULE NODE - Glass-box design with hub glow
// Color discipline: nodes neutral, edges carry semantic meaning
// =============================================================================
const ModuleNode = memo(({ data }: NodeProps) => {
  const nodeData = data as {
    label: string;
    marketingName?: string;
    semanticId?: string;  // Technical ID from Neo4j
    description?: string;
    icon: LucideIcon;
    isHighlighted: boolean;
    isFaded: boolean;
    capabilityCount: number;
    featureCount: number;
    flowInCount: number;
    flowOutCount: number;
    isHub?: boolean; // Larger node for Edge (the hub)
  };
  const Icon = nodeData.icon || Package;
  const displayName = nodeData.marketingName || nodeData.label;
  const isHub = nodeData.isHub;
  // Show semantic_id as technical ID if available, otherwise fall back to label
  const technicalId = nodeData.semanticId || (nodeData.marketingName ? nodeData.label : null);

  return (
    <div
      className={cn(
        'relative transition-all duration-300',
        nodeData.isFaded && 'opacity-20',
        nodeData.isHighlighted && 'scale-105'
      )}
    >
      {/* Input handles on left */}
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3 !h-3 !bg-slate-400 !border-2 !border-background"
      />

      {/* Glass-box card with hub glow */}
      <div
        className={cn(
          'rounded-xl border transition-all duration-300',
          // Glass-box effect
          'bg-white/80 dark:bg-slate-900/60 backdrop-blur-md',
          'border-slate-200/50 dark:border-slate-700/50',
          isHub ? 'w-[260px]' : 'w-[200px]',
          // Hub glow effect (cyan aura)
          isHub && 'shadow-lg shadow-cyan-500/20 dark:shadow-cyan-400/15',
          // Highlight states
          nodeData.isHighlighted && 'border-slate-400 dark:border-slate-500 shadow-md',
          nodeData.isHighlighted && isHub && 'shadow-xl shadow-cyan-500/30'
        )}
      >
        {/* Header */}
        <div className={cn(
          'flex items-center gap-2 border-b border-slate-200/50 dark:border-slate-700/50',
          isHub ? 'px-4 py-3' : 'px-3 py-2'
        )}>
          <div className={cn(
            'rounded-lg flex items-center justify-center',
            'bg-slate-100/80 dark:bg-slate-800/80',
            isHub ? 'w-11 h-11' : 'w-8 h-8'
          )}>
            <Icon className={cn(
              isHub ? 'text-cyan-600 dark:text-cyan-400' : 'text-slate-500 dark:text-slate-400',
              isHub ? 'w-6 h-6' : 'w-4 h-4'
            )} />
          </div>
          <div className="flex-1 min-w-0">
            <span className={cn(
              'font-semibold text-slate-800 dark:text-slate-100 block truncate',
              isHub ? 'text-base' : 'text-sm'
            )}>
              {displayName}
            </span>
            {technicalId && (
              <span className="text-[9px] text-slate-400 dark:text-slate-500 truncate block font-mono">
                {technicalId}
              </span>
            )}
          </div>
        </div>

        {/* Description */}
        {nodeData.description && (
          <div className={cn('px-3 py-2', isHub && 'px-4')}>
            <p className={cn(
              'text-slate-500 dark:text-slate-400',
              isHub ? 'text-xs line-clamp-3' : 'text-[11px] line-clamp-2'
            )}>
              {nodeData.description}
            </p>
          </div>
        )}

        {/* Stats footer */}
        <div className={cn(
          'flex items-center justify-between rounded-b-xl',
          'bg-slate-100/50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400',
          isHub ? 'px-4 py-2 text-xs' : 'px-3 py-1.5 text-[10px]'
        )}>
          <span>{nodeData.flowInCount} in / {nodeData.flowOutCount} out</span>
        </div>
      </div>

      {/* Output handles on right */}
      <Handle
        type="source"
        position={Position.Right}
        className="!w-3 !h-3 !bg-slate-400 !border-2 !border-background"
      />
    </div>
  );
});
ModuleNode.displayName = 'ModuleNode';

// =============================================================================
// LANE HEADER NODE - Column labels at top
// =============================================================================
const LaneHeaderNode = memo(({ data }: NodeProps) => {
  const headerData = data as { label: string; subtitle?: string };
  return (
    <div className="text-center pointer-events-none select-none">
      <div className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
        {headerData.label}
      </div>
      {headerData.subtitle && (
        <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">
          {headerData.subtitle}
        </div>
      )}
    </div>
  );
});
LaneHeaderNode.displayName = 'LaneHeaderNode';

// =============================================================================
// ZONE BACKGROUND NODE - Swimlane backdrop for column groups
// =============================================================================
const ZoneBackgroundNode = memo(({ data }: NodeProps) => {
  const zoneData = data as {
    width: number;
    height: number;
    color: string;
    label: string;
  };

  return (
    <div
      className="relative rounded-2xl pointer-events-none select-none"
      style={{
        width: zoneData.width,
        height: zoneData.height,
        backgroundColor: zoneData.color,
      }}
    >
      {/* Zone label at bottom center */}
      <div className="absolute bottom-2 left-0 right-0 text-center">
        <span className="text-[9px] uppercase tracking-widest text-slate-400/60 dark:text-slate-500/40 font-medium">
          {zoneData.label}
        </span>
      </div>
    </div>
  );
});
ZoneBackgroundNode.displayName = 'ZoneBackgroundNode';

const nodeTypes = {
  module: ModuleNode,
  laneHeader: LaneHeaderNode,
  zoneBackground: ZoneBackgroundNode,
};

// =============================================================================
// LAYOUT FUNCTIONS - Uses merged response from /docs/arch/flow
// =============================================================================
function createFlowLayout(
  data: ArchData,
  hoveredNode: string | null,
  lockedNode: string | null
): { nodes: Node[]; edges: Edge[]; nodeIdMap: Map<string, string> } {
  const archNodes = data.nodes;
  const archEdges = data.edges;
  const flowEdgeStyles = data.flow_edge_styles || {};

  // Build flow in/out counts from edges
  const flowInCount = new Map<string, number>();
  const flowOutCount = new Map<string, number>();

  archEdges.forEach((e) => {
    flowOutCount.set(e.source, (flowOutCount.get(e.source) || 0) + 1);
    flowInCount.set(e.target, (flowInCount.get(e.target) || 0) + 1);
  });

  // Determine highlighted state
  const highlightedNodes = new Set<string>();
  const highlightedEdges = new Set<string>();

  if (hoveredNode || lockedNode) {
    const targetNode = lockedNode || hoveredNode;
    highlightedNodes.add(targetNode!);

    // Find connected flows
    archEdges.forEach((e) => {
      if (e.source === targetNode || e.target === targetNode) {
        highlightedNodes.add(e.source);
        highlightedNodes.add(e.target);
        highlightedEdges.add(e.id);
      }
    });
  }

  const hasHighlight = highlightedNodes.size > 0;

  // Grid layout constants
  const colWidth = 240;
  const rowHeight = 160;
  const startX = 60;
  const startY = 60; // Space for lane headers
  const headerY = 10;
  const zonePadding = 20; // Padding around zones

  // Lane headers configuration
  const laneHeaders: Record<number, { label: string; subtitle?: string }> = {
    0: { label: 'ERP', subtitle: 'Odoo' },
    1: { label: 'Addons', subtitle: 'Odoo Modules' },
    2: { label: 'Pipelines', subtitle: 'Async' },
    3: { label: 'Edge', subtitle: 'Platform' },
    4: { label: 'Capabilities', subtitle: 'Neo4j' },
    5: { label: 'External', subtitle: 'Connectors' },
    6: { label: 'Frontend', subtitle: 'UI' },
  };

  // Swimlane zones configuration
  interface ZoneConfig {
    label: string;
    startCol: number;
    endCol: number;
    color: string;
  }
  const zones: ZoneConfig[] = [
    { label: 'Odoo Ecosystem', startCol: 0, endCol: 1, color: 'rgba(148, 163, 184, 0.06)' }, // slate
    { label: 'Transport', startCol: 2, endCol: 2, color: 'rgba(251, 191, 36, 0.04)' }, // amber
    { label: 'Edge Brain', startCol: 3, endCol: 4, color: 'rgba(34, 211, 238, 0.05)' }, // cyan
    { label: 'External', startCol: 5, endCol: 6, color: 'rgba(168, 85, 247, 0.04)' }, // purple
  ];

  // Calculate nodes per column for vertical centering
  const nodesPerColumn = new Map<number, number>();
  archNodes.forEach((node) => {
    if (node.layout) {
      const col = node.layout.column;
      nodesPerColumn.set(col, (nodesPerColumn.get(col) || 0) + 1);
    }
  });

  // Find max rows to determine vertical centering offset
  const maxRows = Math.max(...Array.from(nodesPerColumn.values()), 1);

  const nodes: Node[] = [];
  const nodeIdMap = new Map<string, string>(); // ReactFlow node id -> arch node label

  // Calculate zone dimensions based on max rows
  const zoneHeight = maxRows * rowHeight + zonePadding * 2;

  // Add zone background nodes FIRST (so they render behind everything)
  zones.forEach((zone, index) => {
    const colSpan = zone.endCol - zone.startCol + 1;
    const zoneWidth = colSpan * colWidth + zonePadding;
    const zoneX = startX + zone.startCol * colWidth - zonePadding / 2;
    const zoneY = startY - zonePadding;

    nodes.push({
      id: `zone_${index}`,
      type: 'zoneBackground',
      position: { x: zoneX, y: zoneY },
      data: {
        width: zoneWidth,
        height: zoneHeight,
        color: zone.color,
        label: zone.label,
      },
      draggable: false,
      selectable: false,
      zIndex: -1, // Ensure it's behind other nodes
    });
  });

  // Add lane header nodes
  const usedColumns = new Set<number>();
  archNodes.forEach((node) => {
    if (node.layout) usedColumns.add(node.layout.column);
  });

  usedColumns.forEach((col) => {
    const header = laneHeaders[col];
    if (header) {
      nodes.push({
        id: `header_${col}`,
        type: 'laneHeader',
        position: { x: startX + col * colWidth, y: headerY },
        data: { label: header.label, subtitle: header.subtitle },
        draggable: false,
        selectable: false,
      });
    }
  });

  // Track row indices per column for vertical centering
  const columnRowIndex = new Map<number, number>();

  archNodes.forEach((archNode, index) => {
    // Layout: use layout from response or auto-layout
    let x: number, y: number;
    if (archNode.layout) {
      const col = archNode.layout.column;
      const colNodeCount = nodesPerColumn.get(col) || 1;
      // Vertical centering: offset based on how many nodes in this column vs max
      const verticalOffset = ((maxRows - colNodeCount) * rowHeight) / 2;

      x = startX + col * colWidth;
      y = startY + archNode.layout.row * rowHeight + verticalOffset;
    } else {
      // Auto-layout fallback
      const cols = Math.max(3, Math.ceil(Math.sqrt(archNodes.length)));
      x = startX + (index % cols) * colWidth;
      y = startY + Math.floor(index / cols) * rowHeight;
    }

    const isHighlighted = highlightedNodes.has(archNode.label);
    const isFaded = hasHighlight && !isHighlighted;

    // Mark klyde_edge as the hub (larger node)
    const isHub = archNode.label === 'klyde_edge';

    const nodeId = `node_${index}`;
    nodeIdMap.set(nodeId, archNode.label);

    nodes.push({
      id: nodeId,
      type: 'module',
      position: { x, y },
      data: {
        label: archNode.label,
        marketingName: archNode.marketing_name,
        semanticId: archNode.semantic_id,
        description: archNode.description,
        icon: getIconComponent(archNode.icon),
        isHighlighted,
        isFaded,
        isHub,
        capabilityCount: 0, // Not applicable for flow view
        featureCount: 0,
        flowInCount: flowInCount.get(archNode.label) || 0,
        flowOutCount: flowOutCount.get(archNode.label) || 0,
      },
    });
  });

  // Create edges from flow data
  const edges: Edge[] = [];
  const labelToNodeId = new Map<string, string>();
  nodeIdMap.forEach((label, nodeId) => {
    labelToNodeId.set(label, nodeId);
  });

  archEdges.forEach((archEdge) => {
    const sourceNodeId = labelToNodeId.get(archEdge.source);
    const targetNodeId = labelToNodeId.get(archEdge.target);

    if (sourceNodeId && targetNodeId) {
      const isHighlighted = highlightedEdges.has(archEdge.id);
      const isFaded = hasHighlight && !isHighlighted;

      // Get edge style from response
      const edgeStyle = flowEdgeStyles[archEdge.type];
      const color = edgeStyle?.color || '#4DD0E1';
      const strokeDasharray = edgeStyle?.stroke_dasharray;

      edges.push({
        id: archEdge.id,
        source: sourceNodeId,
        target: targetNodeId,
        type: 'smoothstep',
        animated: isHighlighted,
        style: {
          stroke: color,
          strokeWidth: isHighlighted ? 3 : 2,
          strokeDasharray,
          opacity: isFaded ? 0.15 : isHighlighted ? 1 : 0.6,
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color,
          width: 20,
          height: 20,
        },
        label: isHighlighted ? edgeStyle?.label : undefined,
        labelStyle: { fill: color, fontSize: 10, fontWeight: 500 },
        labelBgStyle: { fill: 'hsl(var(--card))', fillOpacity: 0.9 },
        labelBgPadding: [4, 2] as [number, number],
        labelBgBorderRadius: 4,
      });
    }
  });

  return { nodes, edges, nodeIdMap };
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================
export function ArchFlowWidget() {
  const [data, setData] = useState<ArchData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [lockedNode, setLockedNode] = useState<string | null>(null);
  const [nodeIdMap, setNodeIdMap] = useState<Map<string, string>>(new Map());
  const selectNode = useGraphSelectionStore((state) => state.selectNode);

  // Fetch data from /docs/arch/flow (merged response)
  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await api.get<ArchData>('/docs/arch/flow');
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Create layout from merged response
  const { initialNodes, initialEdges } = useMemo(() => {
    if (!data?.nodes) return { initialNodes: [], initialEdges: [] };
    const { nodes, edges, nodeIdMap: idMap } = createFlowLayout(
      data,
      hoveredNode,
      lockedNode
    );
    setNodeIdMap(idMap);
    return { initialNodes: nodes, initialEdges: edges };
  }, [data, hoveredNode, lockedNode]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  // Handlers
  const handleNodeMouseEnter = useCallback((_: React.MouseEvent, node: Node) => {
    if (!lockedNode) {
      const label = nodeIdMap.get(node.id);
      if (label) setHoveredNode(label);
    }
  }, [lockedNode, nodeIdMap]);

  const handleNodeMouseLeave = useCallback(() => {
    if (!lockedNode) {
      setHoveredNode(null);
    }
  }, [lockedNode]);

  const handleNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    const label = nodeIdMap.get(node.id);
    if (!label) return;

    // Toggle lock
    if (lockedNode === label) {
      setLockedNode(null);
      setHoveredNode(null);
    } else {
      setLockedNode(label);
    }

    const archNode = data?.nodes.find((n) => n.label === label);
    if (archNode) {
      selectNode({
        id: archNode.id,
        label: archNode.label,
        type: archNode.type,
      });
    }
  }, [data, lockedNode, nodeIdMap, selectNode]);

  const handlePaneClick = useCallback(() => {
    setLockedNode(null);
    setHoveredNode(null);
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Loader2 className="w-12 h-12 animate-spin mb-4" />
        <p>Loading system flow...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-destructive">
        <Network className="w-12 h-12 mb-4" />
        <p>Failed to load</p>
        <p className="text-sm mt-1">{error}</p>
      </div>
    );
  }

  const nodeCount = data?.nodes.length || 0;
  const flowCount = edges.length;

  if (nodeCount === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Network className="w-12 h-12 mb-4 opacity-50" />
        <p>No nodes available</p>
        <p className="text-sm mt-1">Run architecture sync first</p>
      </div>
    );
  }

  // Get edge styles for legend from response
  const edgeStyles = data?.flow_edge_styles || {};

  return (
    <div className="h-full w-full relative">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeMouseEnter={handleNodeMouseEnter}
        onNodeMouseLeave={handleNodeMouseLeave}
        onNodeClick={handleNodeClick}
        onPaneClick={handlePaneClick}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.5}
        maxZoom={1.5}
        proOptions={{ hideAttribution: true }}
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="hsl(var(--muted-foreground) / 0.15)" />
        <Controls
          className="!bg-card !border-border !rounded-lg [&>button]:!bg-card [&>button]:!border-border [&>button]:!text-foreground [&>button:hover]:!bg-secondary"
        />
      </ReactFlow>

      {/* Flow Legend */}
      {Object.keys(edgeStyles).length > 0 && (
        <div className="absolute bottom-3 left-3 z-10 flex flex-col gap-1.5 bg-card/95 backdrop-blur-sm rounded-lg border border-border p-2.5 text-[10px]">
          <div className="text-[9px] uppercase tracking-wide text-muted-foreground mb-1">Flow Types</div>
          {Object.entries(edgeStyles).map(([type, style]) => (
            <div key={type} className="flex items-center gap-2">
              <div
                className="w-6 h-0.5"
                style={{
                  backgroundColor: style.stroke_dasharray ? 'transparent' : style.color,
                  borderTop: style.stroke_dasharray
                    ? `2px ${style.stroke_dasharray.includes('4 4') ? 'dotted' : 'dashed'} ${style.color}`
                    : undefined,
                  height: style.stroke_dasharray ? 0 : 2,
                }}
              />
              <span className="text-muted-foreground">{style.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Stats */}
      <div className="absolute bottom-3 right-3 z-10 flex items-center gap-3 bg-card/95 backdrop-blur-sm rounded-lg border border-border px-3 py-2 text-[10px] text-muted-foreground">
        <span>{data?.metadata.packages || 0} packages</span>
        <span className="text-border">|</span>
        <span>{flowCount} flows</span>
        <span className="text-border">|</span>
        <span>{data?.metadata.synthetic_count || 0} synthetic</span>
      </div>

      {/* Instructions */}
      <div className="absolute top-3 left-3 z-10 px-3 py-1.5 bg-card/90 backdrop-blur-sm rounded-lg border border-border text-[10px] text-muted-foreground">
        Hover to trace flow • Click to lock • Click background to reset
      </div>

      {/* Locked indicator */}
      {lockedNode && (
        <div className="absolute top-3 right-3 z-10 px-3 py-1.5 bg-primary/10 backdrop-blur-sm rounded-lg border border-primary/30 text-[10px] text-primary">
          Selected: {data?.nodes.find((n) => n.label === lockedNode)?.marketing_name || lockedNode}
        </div>
      )}
    </div>
  );
}
